/* iter230 — NexFlow Pricebook Import (staging preview).
   Safe, read-only-until-approved import of the four NexFlow Production Volume PDFs
   into the new tiered Service Catalog. Nothing publishes until the admin approves.
   Backed by /api/admin/pricebook/import/* and /api/admin/pricebook/migration-report. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, CheckCircle2, ShieldCheck,
  Pencil, X, Archive, UploadCloud,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = ['hsp', 'residential', 'property_management', 'commercial'];
const TIER_LABELS = { hsp: 'HSP', residential: 'Residential', property_management: 'Property Mgmt', commercial: 'Commercial' };
const METHODS = [
  { value: 'flat', label: 'Exact Price' },
  { value: 'estimate_required', label: 'Estimate Required' },
  { value: 'not_available', label: 'Not Available' },
  { value: 'needs_review', label: 'Needs Review' },
];

const priceDisplay = (p) => {
  if (!p) return '—';
  if (p.pricing_method === 'flat') return p.price != null ? `$${Number(p.price).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '$0.00';
  if (p.pricing_method === 'estimate_required') return 'Estimate Required';
  if (p.pricing_method === 'not_available') return 'Not Available';
  return 'Needs Review';
};
const methodColor = (m) => ({
  flat: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  estimate_required: 'text-sky-300 border-sky-500/40 bg-sky-500/10',
  not_available: 'text-[#A3A3A3] border-[#3f3f3f] bg-[#262626]',
  needs_review: 'text-orange-300 border-orange-500/40 bg-orange-500/10',
}[m] || 'text-[#A3A3A3] border-[#3f3f3f] bg-[#262626]');

const SummaryStat = ({ label, value, tone }) => (
  <div className="rounded-lg border border-[#262626] bg-[#0A0A0A] p-3" data-testid={`summary-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <p className={`text-2xl font-bold font-['Outfit'] ${tone || 'text-white'}`}>{value}</p>
    <p className="text-[11px] text-[#A3A3A3] mt-0.5">{label}</p>
  </div>
);

const PricebookImport = () => {
  const [data, setData] = useState(null);
  const [migration, setMigration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | review | v1..v4
  const [editRow, setEditRow] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [reviewed, setReviewed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([
        axios.get(`${API}/admin/pricebook/import/preview`, { withCredentials: true }),
        axios.get(`${API}/admin/pricebook/migration-report`, { withCredentials: true }),
      ]);
      setData(p.data); setMigration(m.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load import preview'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const rebuild = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.post(`${API}/admin/pricebook/import/preview`, {}, { withCredentials: true });
      setData(d);
      toast.success('New draft import built from the four Volume PDFs');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  const startNewImport = async () => {
    if (!window.confirm('Start a NEW import draft? This does not touch the currently published catalog until you approve & publish the new draft.')) return;
    await rebuild();
  };

  const saveEdit = async (row) => {
    try {
      const { data: updated } = await axios.put(`${API}/admin/pricebook/import/rows/${row.row_id}`, {
        include: row.include, category: row.category, name: row.name,
        import_notes: row.import_notes, profiles: row.profiles,
      }, { withCredentials: true });
      setData((d) => ({ ...d, rows: d.rows.map((r) => (r.row_id === row.row_id ? updated : r)) }));
      setEditRow(null);
      toast.success('Row updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleInclude = async (row) => {
    try {
      const { data: updated } = await axios.put(`${API}/admin/pricebook/import/rows/${row.row_id}`, { include: !row.include }, { withCredentials: true });
      setData((d) => ({ ...d, rows: d.rows.map((r) => (r.row_id === row.row_id ? updated : r)) }));
    } catch (e) { toast.error('Failed'); }
  };

  const publish = async () => {
    if (!window.confirm('Publish the approved services into the live Service Catalog? Only included, non-blocking rows are created. This is idempotent (safe to rerun).')) return;
    setPublishing(true);
    try {
      const { data: res } = await axios.post(`${API}/admin/pricebook/import/commit`, { batch_id: data.batch_id, confirm: true }, { withCredentials: true });
      toast.success(`Published: ${res.created} created, ${res.updated} updated, ${res.flagged_skipped} flagged/skipped`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to publish'); }
    setPublishing(false);
  };

  const archiveOld = async () => {
    try {
      const { data: res } = await axios.post(`${API}/admin/pricebook/import/archive-old`, {}, { withCredentials: true });
      toast.success(`Archived ${res.archived} old item(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const rows = data?.rows || [];
  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    if (filter === 'review') return rows.filter((r) => (r.warnings || []).length > 0);
    const v = Number(filter.replace('v', ''));
    return rows.filter((r) => r.source_volume === v);
  }, [rows, filter]);

  const samples = useMemo(() => {
    const a = rows.find((r) => r.sku === 'TOI-001');
    const b = rows.find((r) => r.sku === 'COM-005');
    return [a, b].filter(Boolean);
  }, [rows]);

  const s = data?.summary || {};
  const mode = data?.mode || (data?.status === 'committed' ? 'published' : 'draft');
  const isDraft = mode === 'draft';

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="pricebook-import-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-white font-['Outfit'] text-3xl font-bold flex items-center gap-2"><UploadCloud className="w-7 h-7 text-[#D4AF37]" /> Pricebook Import</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">NexFlow Master Service Catalog — admin import management (Production Volumes 1–4).</p>
        </div>
        <div className="flex items-center gap-2">
          {isDraft ? (
            <>
              <button data-testid="rebuild-preview-btn" onClick={rebuild} disabled={loading} className="inline-flex items-center gap-2 text-sm font-bold px-3 py-2 rounded-lg border border-[#3f3f3f] text-white hover:bg-[#262626]"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Rebuild Preview</button>
              <label className="flex items-center gap-1.5 text-xs text-[#A3A3A3] select-none" data-testid="reviewed-checkbox-label"><input type="checkbox" data-testid="reviewed-checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} /> I reviewed all rows</label>
              <button data-testid="publish-btn" onClick={publish} disabled={publishing || !data || !reviewed} title={!reviewed ? 'Confirm you reviewed all rows first' : ''} className="inline-flex items-center gap-2 text-sm font-bold px-3 py-2 rounded-lg bg-[#D4AF37] text-black hover:bg-[#c4a032] disabled:opacity-50 disabled:cursor-not-allowed"><CheckCircle2 className="w-4 h-4" /> Approve &amp; Publish</button>
            </>
          ) : (
            <button data-testid="start-new-import-btn" onClick={startNewImport} disabled={loading} className="inline-flex items-center gap-2 text-sm font-bold px-3 py-2 rounded-lg border border-[#3f3f3f] text-white hover:bg-[#262626]"><UploadCloud className="w-4 h-4" /> Start New Import</button>
          )}
        </div>
      </div>

      {/* Published-batch state (no longer a staging preview) */}
      {mode === 'published' && (
        <div className="rounded-lg border border-emerald-600/50 bg-emerald-500/10 p-4 mb-5" data-testid="published-banner">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-6 h-6 text-emerald-300 shrink-0" />
            <div className="flex-1">
              <p className="text-emerald-100 font-bold text-lg">Published Successfully</p>
              <p className="text-emerald-100/80 text-sm">The current Service Catalog is live. This batch is not awaiting approval.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                <SummaryStat label="Live Catalog" value={data.live_catalog_count} tone="text-emerald-300" />
                <SummaryStat label="Published" value={data.published_count ?? '—'} />
                <SummaryStat label="Warnings" value={data.warnings_count ?? 0} tone={data.warnings_count ? 'text-orange-300' : 'text-white'} />
                <SummaryStat label="Source" value={(data.source || '').split(' ').slice(-2).join(' ') || '—'} />
              </div>
              <p className="text-[11px] text-emerald-100/70 mt-2">
                Batch <span className="font-mono">{data.batch_id?.slice(0, 8)}</span>
                {data.published_at ? ` · published ${new Date(data.published_at).toLocaleString()}` : ''}
                {data.published_by ? ` · by ${data.published_by}` : ''} · {data.source}
              </p>
            </div>
          </div>
        </div>
      )}

      {mode === 'empty' && (
        <div className="rounded-lg border border-[#262626] bg-[#0A0A0A] p-6 mb-5 text-center" data-testid="empty-import">
          <p className="text-[#A3A3A3]">No catalog has been imported yet. Use <b className="text-white">Start New Import</b> to stage the Volume PDFs.</p>
        </div>
      )}

      {isDraft && (
      <div className="rounded-lg border border-sky-600/50 bg-sky-500/10 p-3 mb-5 flex items-start gap-2" data-testid="staging-banner">
        <ShieldCheck className="w-5 h-5 text-sky-300 shrink-0 mt-0.5" />
        <p className="text-sky-100 text-sm">This is a <b>staging preview</b>. Nothing is published to the live catalog until you press <b>Approve &amp; Publish</b>. Each tier price is an <b>independent exact value</b> — no percentage formulas are stored. Custom = Estimate Required, N/A = Not Available (never $0).</p>
      </div>
      )}

      {/* Summary (draft only) */}
      {isDraft && (
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-6" data-testid="import-summary">
        <SummaryStat label="Total Extracted" value={s.total_extracted ?? '—'} />
        <SummaryStat label="Valid" value={s.valid_services ?? '—'} tone="text-emerald-300" />
        <SummaryStat label="Need Review" value={s.rows_requiring_review ?? '—'} tone="text-orange-300" />
        <SummaryStat label="Duplicate SKUs" value={s.duplicate_skus ?? '—'} tone={s.duplicate_skus ? 'text-red-300' : 'text-white'} />
        <SummaryStat label="Missing Prices" value={s.missing_prices ?? '—'} tone={s.missing_prices ? 'text-red-300' : 'text-white'} />
        <SummaryStat label="Missing Category" value={s.missing_categories ?? '—'} tone={s.missing_categories ? 'text-red-300' : 'text-white'} />
        <SummaryStat label="Estimate Required" value={s.estimate_required_values ?? '—'} tone="text-sky-300" />
        <SummaryStat label="Not Available" value={s.not_available_values ?? '—'} />
        <SummaryStat label="Existing Conflicts" value={s.existing_sku_conflicts ?? '—'} tone={s.existing_sku_conflicts ? 'text-red-300' : 'text-white'} />
        <SummaryStat label="Historical Refs" value={s.historical_relationships ?? '—'} tone={s.historical_relationships ? 'text-orange-300' : 'text-white'} />
        <SummaryStat label="V1 / V2" value={`${s.by_volume?.['1'] ?? 0} / ${s.by_volume?.['2'] ?? 0}`} />
        <SummaryStat label="V3 / V4" value={`${s.by_volume?.['3'] ?? 0} / ${s.by_volume?.['4'] ?? 0}`} />
      </div>
      )}

      {/* Two complete sample records */}
      {isDraft && samples.length > 0 && (
        <div className="mb-6" data-testid="sample-records">
          <h2 className="text-white font-bold text-lg mb-2">Sample Records (full canonical structure)</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {samples.map((r) => (
              <div key={r.sku} data-testid={`sample-${r.sku}`} className="rounded-lg border border-[#262626] bg-[#0A0A0A] p-4">
                <div className="flex items-center justify-between">
                  <p className="text-white font-bold">{r.name}</p>
                  <span className="text-xs font-mono text-[#D4AF37]">{r.sku}</span>
                </div>
                <p className="text-xs text-[#A3A3A3] mt-0.5">{r.category} · {r.source_volume_label} · p.{r.source_page}</p>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {TIERS.map((t) => (
                    <div key={t} className="rounded border border-[#262626] p-2">
                      <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">{TIER_LABELS[t]}</p>
                      <p className="text-white font-bold text-sm">{priceDisplay(r.profiles[t])}</p>
                      <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${methodColor(r.profiles[t].pricing_method)}`}>{r.profiles[t].pricing_method.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
                {r.needs_completion?.length > 0 && (
                  <p className="text-[11px] text-orange-300 mt-2">Needs completion later: {r.needs_completion.join(', ')}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Migration report */}
      {migration && (
        <div className="mb-6 rounded-lg border border-[#262626] bg-[#0A0A0A] p-4" data-testid="migration-report">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-white font-bold text-lg flex items-center gap-2"><Archive className="w-5 h-5 text-[#A3A3A3]" /> Migration Safety Report</h2>
            <button data-testid="archive-old-btn" onClick={archiveOld} className="text-xs font-bold px-2.5 py-1 rounded border border-[#3f3f3f] text-[#A3A3A3] hover:text-white">Archive Old Catalog</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            <SummaryStat label="Old Items" value={migration.old_catalog_count} />
            <SummaryStat label="Referenced by History" value={migration.old_referenced_count} tone={migration.old_referenced_count ? 'text-orange-300' : 'text-white'} />
            <SummaryStat label="Archived" value={migration.archived_count} />
            <SummaryStat label="New Catalog" value={migration.new_catalog_count} tone="text-emerald-300" />
          </div>
          <p className="text-[11px] text-[#A3A3A3] mt-2">{migration.note}</p>
        </div>
      )}

      {/* Filters */}
      {isDraft && (<>
      <div className="flex flex-wrap gap-2 mb-2">
        {[['all', 'All'], ['review', 'Needs Review'], ['v1', 'Volume 1'], ['v2', 'Volume 2'], ['v3', 'Volume 3'], ['v4', 'Volume 4']].map(([k, lbl]) => (
          <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)} className={`text-xs font-bold px-2.5 py-1 rounded border ${filter === k ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#3f3f3f] text-[#A3A3A3]'}`}>{lbl}</button>
        ))}
        <span className="text-xs text-[#525252] self-center ml-1">{filtered.length} row(s)</span>
      </div>

      {/* Rows */}
      <div className="rounded-lg border border-[#262626] overflow-hidden" data-testid="import-rows">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#141414] text-[#A3A3A3] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Incl.</th>
                <th className="px-3 py-2 text-left">SKU</th>
                <th className="px-3 py-2 text-left">Service</th>
                <th className="px-3 py-2 text-left">Category</th>
                {TIERS.map((t) => <th key={t} className="px-3 py-2 text-right">{TIER_LABELS[t]}</th>)}
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Warnings</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.row_id} data-testid={`import-row-${r.sku}`} className={`border-t border-[#1f1f1f] ${!r.include ? 'opacity-40' : ''} ${(r.warnings || []).length ? 'bg-orange-500/[0.04]' : ''}`}>
                  <td className="px-3 py-2"><input type="checkbox" data-testid={`include-${r.sku}`} checked={!!r.include} onChange={() => toggleInclude(r)} /></td>
                  <td className="px-3 py-2 font-mono text-[#D4AF37] whitespace-nowrap">{r.sku}</td>
                  <td className="px-3 py-2 text-white">{r.name}</td>
                  <td className="px-3 py-2 text-[#A3A3A3]">{r.category}</td>
                  {TIERS.map((t) => (
                    <td key={t} className="px-3 py-2 text-right whitespace-nowrap" data-testid={`price-${r.sku}-${t}`}>
                      <span className={`text-white ${r.profiles[t].pricing_method !== 'flat' ? 'text-xs text-[#A3A3A3]' : ''}`}>{priceDisplay(r.profiles[t])}</span>
                    </td>
                  ))}
                  <td className="px-3 py-2 text-[11px] text-[#A3A3A3] whitespace-nowrap">V{r.source_volume} p.{r.source_page}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(r.warnings || []).map((w, i) => (
                        <span key={i} className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${w.code.includes('duplicate') || w.code.includes('conflict') || w.code.includes('missing') ? 'text-red-300 border-red-500/40 bg-red-500/10' : 'text-orange-300 border-orange-500/40 bg-orange-500/10'}`}>{w.label}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2"><button data-testid={`edit-${r.sku}`} onClick={() => setEditRow(JSON.parse(JSON.stringify(r)))} className="text-[#A3A3A3] hover:text-white"><Pencil className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>)}

      {/* Edit dialog */}
      {editRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="edit-row-dialog">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditRow(null)} />
          <div className="relative bg-[#141414] border border-[#262626] rounded-lg w-full max-w-lg max-h-[88vh] overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-bold">{editRow.sku} — Edit</h3>
              <button onClick={() => setEditRow(null)} className="text-[#A3A3A3] hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <label className="block text-xs text-[#A3A3A3] mb-1">Service Name</label>
            <input className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white mb-2" value={editRow.name} onChange={(e) => setEditRow({ ...editRow, name: e.target.value })} />
            <label className="block text-xs text-[#A3A3A3] mb-1">Category</label>
            <input className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white mb-3" value={editRow.category} onChange={(e) => setEditRow({ ...editRow, category: e.target.value })} />
            <p className="text-xs text-[#A3A3A3] mb-1 font-bold">Independent Tier Prices</p>
            {TIERS.map((t) => (
              <div key={t} className="flex items-center gap-2 mb-2">
                <span className="text-xs text-white w-28 shrink-0">{TIER_LABELS[t]}</span>
                <select data-testid={`edit-method-${t}`} className="bg-[#0A0A0A] border border-[#262626] rounded px-1.5 py-1 text-white text-xs" value={editRow.profiles[t].pricing_method}
                  onChange={(e) => setEditRow({ ...editRow, profiles: { ...editRow.profiles, [t]: { ...editRow.profiles[t], pricing_method: e.target.value } } })}>
                  {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <input type="number" data-testid={`edit-price-${t}`} disabled={editRow.profiles[t].pricing_method !== 'flat'} className="w-24 bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1 text-white text-xs disabled:opacity-40" value={editRow.profiles[t].price ?? ''}
                  onChange={(e) => setEditRow({ ...editRow, profiles: { ...editRow.profiles, [t]: { ...editRow.profiles[t], price: e.target.value === '' ? null : Number(e.target.value) } } })} />
              </div>
            ))}
            <label className="block text-xs text-[#A3A3A3] mb-1 mt-2">Import Notes</label>
            <textarea className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white text-sm mb-3" rows={2} value={editRow.import_notes || ''} onChange={(e) => setEditRow({ ...editRow, import_notes: e.target.value })} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditRow(null)} className="text-sm px-3 py-1.5 rounded border border-[#3f3f3f] text-[#A3A3A3]">Cancel</button>
              <button data-testid="save-edit-btn" onClick={() => saveEdit(editRow)} className="text-sm font-bold px-3 py-1.5 rounded bg-[#D4AF37] text-black">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricebookImport;
